export type ClientFilterFieldType =
	| 'string'
	| 'number'
	| 'boolean'
	| 'enum'
	| 'date'

export type ClientFieldFilterOp = 'eq' | 'contains' | 'empty' | 'notEmpty'

export interface ClientFilterFieldOption {
	value: string
	label: string
}

export interface ClientFilterField {
	field: string
	label: string
	group: string
	type: ClientFilterFieldType
	options?: ClientFilterFieldOption[]
}

export interface ClientFieldFilter {
	field: string
	op: ClientFieldFilterOp
	value?: string
}

const options = (values: string[]): ClientFilterFieldOption[] =>
	values.map(value => ({ value, label: value.replace(/_/g, ' ') }))

const stringField = (
	field: string,
	label: string,
	group: string
): ClientFilterField => ({ field, label, group, type: 'string' })

const booleanField = (
	field: string,
	label: string,
	group: string
): ClientFilterField => ({ field, label, group, type: 'boolean' })

const numberField = (
	field: string,
	label: string,
	group: string
): ClientFilterField => ({ field, label, group, type: 'number' })

const dateField = (
	field: string,
	label: string,
	group: string
): ClientFilterField => ({ field, label, group, type: 'date' })

const enumField = (
	field: string,
	label: string,
	group: string,
	values: string[]
): ClientFilterField => ({
	field,
	label,
	group,
	type: 'enum',
	options: options(values),
})

export const clientFilterFields: ClientFilterField[] = [
	stringField('organizationName', 'Organization name', 'Identity'),
	stringField('clientId', 'Client ID', 'Identity'),
	stringField('email', 'Email', 'Identity'),
	stringField('name', 'Contact name', 'Identity'),
	stringField('website', 'Website', 'Identity'),
	stringField('mobile', 'Mobile', 'Identity'),
	stringField('mobileCode', 'Mobile country code', 'Identity'),
	stringField('notificationEmails', 'Notification emails', 'Identity'),
	stringField('invoiceEmails', 'Invoice emails', 'Identity'),
	stringField('city', 'City', 'Location'),
	stringField('state', 'State', 'Location'),
	stringField('country', 'Country', 'Location'),
	stringField('address', 'Address', 'Location'),
	stringField('pinCode', 'PIN code', 'Location'),
	booleanField('active', 'Active', 'Account'),
	booleanField('accountSuspended', 'Suspended', 'Account'),
	booleanField('isFreePlan', 'Free plan', 'Account'),
	booleanField('isFreeTrial', 'Free trial', 'Account'),
	booleanField('isEmailVerified', 'Email verified', 'Account'),
	booleanField('optInWhatsAppCheck', 'WhatsApp opt-in', 'Account'),
	booleanField('tdsEnabled', 'TDS enabled', 'Account'),
	booleanField('whatsyTrigger', 'Whatsy trigger', 'Account'),
	booleanField('shouldGenerateAutoInvoice', 'Auto invoice', 'Account'),
	enumField('platformMode', 'Platform mode', 'Account', [
		'DEMO',
		'LIVE',
		'FREE_PLAN',
	]),
	enumField('selectedEDGEPlatform', 'Platform', 'Account', [
		'DASH_CX',
		'EDGE_CX',
		'TURBO_CX',
	]),
	enumField('platformStack', 'Platform stack', 'Account', ['V1', 'V2']),
	enumField('currency_selected', 'Currency', 'Account', [
		'USD',
		'INR',
		'EUR',
		'IDR',
		'AUD',
		'GBP',
	]),
	enumField('numberOfEmployees', 'Employees', 'Account', [
		'SELF_EMPLOYED',
		'1-10',
		'11-50',
		'51-200',
		'201-500',
		'501-1000',
		'1001-5000',
		'5001-10,000',
		'10,001+',
	]),
	enumField('subscription', 'Subscription', 'Account', [
		'SUBSCRIPTION',
		'RENEWAL_SUBSCRIPTION_REQUIRED',
	]),
	enumField('winyPlanKey', 'Winy plan', 'Account', ['WINY', 'WINY_PRO']),
	numberField('broadcastLimit', 'Broadcast limit', 'Account'),
	numberField('storageUsed', 'Storage used (KB)', 'Account'),
	numberField('whatcxCharge', 'Charge', 'Billing'),
	numberField('whatcxChargeInbound', 'Inbound charge', 'Billing'),
	numberField('whatcxChargeOutbound', 'Outbound charge', 'Billing'),
	booleanField('lowWalletBalanceNotify', 'Low balance notified', 'Billing'),
	booleanField(
		'insufficientBalanceNotify',
		'Insufficient balance notified',
		'Billing'
	),
	dateField('nextBillingDate', 'Next billing date', 'Billing'),
	dateField('createdAt', 'Created on', 'Dates'),
	dateField('updatedAt', 'Updated on', 'Dates'),
	dateField('archivedAt', 'Archived on', 'Dates'),
	enumField('vendorSelected', 'Vendor', 'WhatsApp', [
		'GUPSHUP',
		'GUPSHUP_ENTERPRISE',
		'THREE_SIXTY_DIALOG',
		'WHATSAPP_CLOUD',
		'AIRTEL',
		'PINNACLE',
	]),
	stringField('appName', 'App name', 'WhatsApp'),
	stringField('sourceWaNumber', 'WhatsApp number', 'WhatsApp'),
	stringField('wabaId', 'WABA ID', 'WhatsApp'),
	stringField('wa_phone_id', 'Phone ID', 'WhatsApp'),
	enumField('currentLimit', 'Messaging limit', 'WhatsApp', [
		'TIER_0K',
		'TIER_1K',
		'TIER_10K',
		'TIER_100K',
		'TIER_UNLIMITED',
	]),
	enumField('waPhoneStatus', 'Phone status', 'WhatsApp', [
		'NA',
		'FLAGGED',
		'RESTRICTED',
	]),
	enumField('waQualityRating', 'Quality rating', 'WhatsApp', [
		'GREEN',
		'YELLOW',
		'RED',
	]),
	booleanField('isWaVerified', 'WhatsApp verified', 'WhatsApp'),
	booleanField('is_account_setup', 'Onboarded', 'WhatsApp'),
	enumField('profileOnboardingMode', 'Onboarding mode', 'WhatsApp', [
		'BSP_ONBOARDING',
		'TSP_SELF_ONBOARDING',
	]),
	stringField('tspProvider', 'TSP provider', 'WhatsApp'),
	enumField('paymentMode', 'Payment mode', 'WhatsApp', [
		'TSP_AIRTEL',
		'TSP_META',
		'TSP_PINNACLE',
		'TSP_PINNACLE_V3',
	]),
	stringField('gupshup_user_id', 'Gupshup user ID', 'WhatsApp'),
	stringField('gupshup_two_way_user', 'Gupshup two-way user', 'WhatsApp'),
	stringField('airtelCustomerId', 'Airtel customer ID', 'WhatsApp'),
	stringField('airtelUsername', 'Airtel username', 'WhatsApp'),
	stringField('ts_clientId', '360 client ID', 'WhatsApp'),
	stringField('ts_channelId', '360 channel ID', 'WhatsApp'),
	stringField('webhookUrl', 'Webhook URL', 'WhatsApp'),
	booleanField('coexistenceEnabled', 'Coexistence enabled', 'WhatsApp'),
	booleanField('isCoexistenceOnboarding', 'Coexistence onboarding', 'WhatsApp'),
	booleanField('historyShared', 'History shared', 'WhatsApp'),
	booleanField(
		'is_template_sample_deleted',
		'Sample templates deleted',
		'WhatsApp'
	),
	booleanField('isTemplateInsightEnabled', 'Template insights', 'WhatsApp'),
	booleanField('metaPaymentStatus', 'Meta payment status', 'WhatsApp'),
	booleanField('whatsappApi', 'WhatsApp channel', 'WhatsApp'),
	booleanField('voiceEnablex', 'Voice channel', 'WhatsApp'),
	enumField('credentialMode', 'AI credential mode', 'WhatsApp', [
		'shared',
		'byok',
	]),
	dateField('limitUpdatedAt', 'Limit updated on', 'WhatsApp'),
	numberField('onBoardingStep', 'Onboarding step', 'Onboarding'),
	numberField('nextStep', 'Next step', 'Onboarding'),
	enumField('onBoardingType', 'Onboarding type', 'Onboarding', ['FREE', 'PAID']),
	booleanField('ensureBusinessPage', 'Business page', 'Onboarding'),
	booleanField('businessManager', 'Business manager', 'Onboarding'),
	booleanField('developerApp', 'Developer app', 'Onboarding'),
	booleanField('whatsAppEnable', 'WhatsApp enabled', 'Onboarding'),
	booleanField('systemUserCreated', 'System user created', 'Onboarding'),
	booleanField('assetsAdded', 'Assets added', 'Onboarding'),
	booleanField('setupWebhook', 'Webhook set up', 'Onboarding'),
	booleanField('setupPayment', 'Payment set up', 'Onboarding'),
	stringField('fbPageUrl', 'Facebook page URL', 'Onboarding'),
	stringField('fbAccountId', 'Facebook account ID', 'Onboarding'),
	stringField('utmSource', 'UTM source', 'Marketing'),
	stringField('utmCampaign', 'UTM campaign', 'Marketing'),
	stringField('utmMedium', 'UTM medium', 'Marketing'),
	stringField('referCode', 'Referral code', 'Marketing'),
	stringField('tags', 'Tag', 'Marketing'),
]

export const clientFilterFieldMap = Object.fromEntries(
	clientFilterFields.map(field => [field.field, field])
)

export const describeClientFieldFilter = (filter: ClientFieldFilter) => {
	const field = clientFilterFieldMap[filter.field]
	const label = field?.label || filter.field
	if (filter.op === 'empty') return `${label} is empty`
	if (filter.op === 'notEmpty') return `${label} is set`
	let { value } = filter
	if (field?.type === 'boolean') {
		value = filter.value === 'true' ? 'yes' : 'no'
	} else if (field?.options) {
		value =
			field.options.find(option => option.value === filter.value)?.label ||
			filter.value
	}
	const match = filter.op === 'contains' ? 'contains' : 'is'
	return `${label} ${match} ${value}`
}
